import { Star } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import hero from "@/assets/hero-shop.jpg";
import nbaPlayoffs from "@/assets/nba-playoffs.jpg";
import trendDraft from "@/assets/trend-draft.jpg";
import trendRealtree from "@/assets/trend-realtree.jpg";
import trendCity from "@/assets/trend-cityconnect.jpg";
import { ProductGrid } from "@/components/ProductGrid";
import { Reveal } from "@/components/Reveal";
import { CountdownTimer } from "@/components/CountdownTimer";
import { useSeo } from "@/hooks/useSeo";

const trending = [
  { title: "NBA PLAYOFFS", img: nbaPlayoffs, to: "/league?sport=NBA" },
  { title: "NFL DRAFT", img: trendDraft, to: "/league?sport=NFL" },
  { title: "STANLEY CUP PLAYOFFS", img: trendRealtree, to: "/league?sport=NHL" },
  { title: "WORLD SERIES", img: trendCity, to: "/league?sport=MLB" },
];

const MARQUEE_ITEMS = [
  "FREE SHIPPING OVER $150+",
  "NEW DROPS EVERY FRIDAY",
  "FITTED FOR THE CITY",
  "JOIN THE MOB",
  "AUTHENTICITY GUARANTEED",
  "WORLDWIDE DELIVERY",
];

const Marquee = () => (
  <div className="bg-foreground text-background overflow-hidden py-5 border-y border-foreground">
    <div className="marquee flex flex-nowrap whitespace-nowrap font-display text-base md:text-xl tracking-widest">
      {Array.from({ length: 2 }).map((_, i) => (
        <ul key={i} className="flex flex-nowrap shrink-0 items-center m-0 p-0 list-none" aria-hidden={i === 1}>
          {MARQUEE_ITEMS.map((t) => (
            <li key={`${i}-${t}`} className="flex items-center shrink-0">
              <span className="px-10">{t}</span>
              <Star className="w-3.5 h-3.5 fill-background shrink-0" />
            </li>
          ))}
        </ul>
      ))}
    </div>
  </div>
);

const Index = () => {
  const [limitedExpired, setLimitedExpired] = useState(false);
  useSeo({
    title: "Brim Mob — Premium Fitted Caps & Streetwear",
    description: "Shop Brim Mob: premium fitted caps, limited drops, and streetwear for the city. New collections every Friday from your favorite teams.",
  });
  return (
    <>
      <section className="relative h-[88vh] w-full overflow-hidden">
        <img
          src={hero}
          alt="Brim Mob retail shop interior"
          width={1920}
          height={1080}
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover scale-105 animate-[page-fade_1.4s_cubic-bezier(0.22,1,0.36,1)_both]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/75 via-foreground/15 to-transparent" />
        <div className="absolute bottom-16 left-0 container">
          <p className="font-display text-background/80 text-sm tracking-widest mb-4 animate-[page-fade_0.7s_cubic-bezier(0.22,1,0.36,1)_0.2s_both]">
            SUMMER '26 — VOLUME 04
          </p>
          <h1 className="font-display text-background text-6xl md:text-8xl leading-none max-w-2xl animate-[page-fade_0.9s_cubic-bezier(0.22,1,0.36,1)_0.35s_both]">
            FITTED.<br />FOR THE MOB.
          </h1>
          <div className="mt-8 flex gap-4 animate-[page-fade_0.9s_cubic-bezier(0.22,1,0.36,1)_0.55s_both]">
            <Link to="/hats" className="btn-shine bg-background text-foreground font-display px-8 py-4 text-base transition-colors duration-300 hover:bg-accent hover:text-accent-foreground">
              SHOP HATS
            </Link>
            <Link to="/drops" className="btn-shine border border-background text-background font-display px-8 py-4 text-base transition-colors duration-300 hover:bg-background hover:text-foreground">
              VIEW DROPS
            </Link>
          </div>
        </div>
      </section>

      <Marquee />

      {/* LIMITED EDITION */}
      <section className="bg-background text-foreground py-16 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent to-transparent animate-pulse" />
        <div className="container">
          <Reveal className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-10">
            <div className="inline-flex items-center gap-4 bg-foreground text-background px-4 py-2">
              <span className="inline-flex items-center gap-1.5 bg-accent text-accent-foreground font-display text-[10px] tracking-widest px-2.5 py-1 animate-[urgency-pulse_1.5s_ease-in-out_infinite]">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-foreground animate-pulse" />
                LIVE
              </span>
              <h2 className="font-display text-3xl md:text-4xl">LIMITED EDITION</h2>
            </div>
            <div className="flex items-center gap-6">
              <CountdownTimer onExpired={setLimitedExpired} />
            </div>
          </Reveal>
          <Reveal delay={120}>
            <ProductGrid first={5} columns={5} query='product_type:"Limited Edition"' soldOut={limitedExpired} />
          </Reveal>
        </div>
      </section>

      {/* NEW ARRIVALS */}
      <section className="bg-background text-foreground py-16 border-t border-background/10">
        <div className="container">
          <Reveal className="flex items-center justify-between mb-10">
            <h2 className="inline-block bg-foreground text-background font-display text-3xl md:text-4xl px-4 py-2">NEW ARRIVALS</h2>
          </Reveal>
          <Reveal delay={120}>
            <ProductGrid first={12} layout="carousel" />
          </Reveal>
        </div>
      </section>

      {/* TRENDING NOW */}
      <section className="bg-foreground text-background py-16 border-t border-background/10">
        <div className="container">
          <Reveal as="h2" className="font-display text-3xl md:text-4xl mb-10">TRENDING NOW</Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {trending.map((t, i) => (
              <Reveal key={t.title} delay={i * 90}>
                <Link to={t.to} className="group block">
                  <div className="aspect-[3/4] overflow-hidden bg-background/5 relative">
                    <img
                      src={t.img}
                      alt={t.title}
                      width={800}
                      height={1024}
                      loading="eager"
                      decoding="async"
                      fetchPriority="high"
                      className="w-full h-full object-cover object-center transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-110"
                    />
                    <div className="pointer-events-none absolute inset-0 bg-foreground/0 group-hover:bg-foreground/20 transition-colors duration-500" />
                  </div>
                  <p className="font-display text-sm tracking-widest text-center mt-4 transition-colors duration-300 group-hover:text-accent">{t.title}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Index;